import { useState } from "react"
import styled from "styled-components"

import { Banner, Button, PinInput } from "@minuthq/meatball-ui-react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { fetchSetupMfaQr, postSetupMfa } from "src/domains/auth/auth-queries"
import { useMutation } from "src/domains/http/use-mutation"
import { useQuery } from "src/domains/http/use-query"
import { useTranslate } from "src/domains/i18n/use-translate"
import backdropImage from "src/ui/assets/buildings-orange-brown.jpg"
import { PageLayout } from "src/ui/page-layout/page-layout"
import { QrCode } from "src/ui/qr-code/qr-code"

export const Route = createFileRoute("/mfa/setup")({
  component: MfaSetup,
})

function MfaSetup() {
  const { tPlaceholder } = useTranslate()

  return (
    <PageLayout
      title={tPlaceholder("Setup multi-factor authentication")}
      description={tPlaceholder(
        "Scan the QR code with your authenticator app to set up MFA. Then enter the code from your authenticator app to complete setup."
      )}
      footer={<Footer />}
      landscapeImageUrl={backdropImage}
      portraitImageUrl={backdropImage}
    >
      <MfaSetupContent />
    </PageLayout>
  )
}

function MfaSetupContent() {
  const [code, setCode] = useState("")
  const { tPlaceholder } = useTranslate()

  const {
    data: qrCodeData,
    isLoading: isLoadingQr,
    isError,
  } = useQuery(fetchSetupMfaQr)

  const {
    mutate: mutateSetupMfa,
    isLoading: isSubmitting,
    isError: isCodeError,
    reset,
  } = useMutation(postSetupMfa)

  function handleCodeChange(code: string) {
    setCode(code)
    if (isCodeError) {
      reset()
    }
  }

  function handleSubmitMfaCode(code: string) {
    void mutateSetupMfa(
      { code },
      {
        onSuccess: (response) => {
          window.location.href = response.redirect_url
        },
      }
    )
  }

  if (isError) {
    return (
      <Banner type="error">
        {tPlaceholder("Something went wrong. Please try again.")}
      </Banner>
    )
  }

  return (
    <>
      <QrCode url={qrCodeData?.qr_code_url ?? null} isLoading={isLoadingQr} />

      {!isLoadingQr && (
        <PinInputBox>
          <PinInput
            maxLength={6}
            value={code}
            onChange={handleCodeChange}
            onComplete={handleSubmitMfaCode}
            loading={isSubmitting}
            error={
              isCodeError && !isSubmitting
                ? tPlaceholder("Invalid code")
                : undefined
            }
          />
        </PinInputBox>
      )}
    </>
  )
}

const PinInputBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

function Footer() {
  const navigate = useNavigate()
  const { tPlaceholder } = useTranslate()

  return (
    <Button
      variant="minimal"
      color="neutral"
      size="small"
      onClick={() => {
        void navigate({ to: "/login" })
      }}
    >
      {tPlaceholder("Return to sign in")}
    </Button>
  )
}
